/**
 * GlobalLoadingOverlay Component
 * Professional loading overlay with centered progress bar
 * Features: Customizable messages, progress display, minimal design
 */

import { useGlobalLoadingContext } from '@/contexts/GlobalLoadingContext';
import { Progress } from '@/components/shadcn/progress';
import { cn } from '@/lib/utils';

export function GlobalLoadingOverlay() {
  const { isLoading, options } = useGlobalLoadingContext();

  if (!isLoading) {
    return null;
  }

  const {
    message = 'Loading...',
    submessage = 'Please wait',
    showProgress = false,
    progress = 0,
    showOverlay = true,
  } = options;

  return (
    <>
      {/* Overlay - Disables interaction and dims the screen */}
      {showOverlay && (
        <div
          className={cn(
            'fixed inset-0 z-[9999] bg-foreground/10 backdrop-blur-sm',
            'transition-opacity duration-300',
            isLoading ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}

      {/* Centered Progress Card */}
      <div className="fixed inset-0 z-[10000] flex items-center justify-center pointer-events-none">
        <div className="bg-background rounded-2xl shadow-2xl border border-border p-8 flex flex-col items-center gap-6 min-w-[320px] max-w-md">

          {/* Animated spinner */}
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>

          {/* Loading Text */}
          <div className="flex flex-col items-center gap-2 text-center">
            <h3 className="text-lg font-semibold text-foreground">
              {message}
            </h3>
            {submessage && (
              <p className="text-sm text-muted-foreground">
                {submessage}
              </p>
            )}
          </div>

          {/* Progress Bar - Larger and centered */}
          <div className="w-full">
            {showProgress && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">Progress</span>
                <span className="text-xs font-bold text-primary">{Math.round(progress)}%</span>
              </div>
            )}
            <Progress
              value={showProgress ? progress : undefined}
              className={cn(
                "h-3 w-full",
                !showProgress && "animate-pulse"
              )}
            />
          </div>
        </div>
      </div>
    </>
  );
}

